import { type Expense, ExpenseStatusEnum, TabTypeEnum, type TripFolder } from "@finway-group/shared/lib/models"
import { matchPath } from "react-router-dom"
import { CompanyScopedRoutePathEnum } from "~Shared/router/RoutePath.enum"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { isFolderExpense } from "~Shared/utils/expense.utils"

interface DashboardTabsVisibility {
    isPurchaseOrderEnabled: boolean
    isPoSkipProcurementEnabled: boolean
    hasReadRightForProcurement: boolean
    isGlobalApprover: boolean
    hasReadRightForExpensesToReview: boolean
    hasReadRightForExpensesToPay: boolean
    hasReadRightForExpensesToExport: boolean
}

/**
 * Which dashboard tabs are available for the current company settings and
 * permissions. Used to fall back the persisted active tab when it becomes
 * unavailable. Keep in sync with the conditional `TabPane`s in
 * `dashboard.container.tsx`.
 */
export const getAvailableDashboardTabs = (visibility: DashboardTabsVisibility): Array<TabTypeEnum> => {
    const tabs: Array<TabTypeEnum> = [TabTypeEnum.INBOX_INVOICES, TabTypeEnum.TO_DOS]

    if (visibility.isPurchaseOrderEnabled && !visibility.isPoSkipProcurementEnabled && visibility.hasReadRightForProcurement) {
        tabs.push(TabTypeEnum.PROCUREMENT)
    }
    if (!visibility.isGlobalApprover) {
        tabs.push(TabTypeEnum.IN_PROGRESS)
    }
    if (visibility.hasReadRightForExpensesToReview) {
        tabs.push(TabTypeEnum.REVIEW)
    }
    if (visibility.hasReadRightForExpensesToPay || visibility.hasReadRightForExpensesToExport) {
        tabs.push(TabTypeEnum.PAY_EXPORT)
    }
    tabs.push(TabTypeEnum.DONE)
    if (visibility.isGlobalApprover) {
        tabs.push(TabTypeEnum.ALL)
    }
    tabs.push(TabTypeEnum.ARCHIVE)

    return tabs
}

export const getCurrentPageInfo = (documentId: string, dependencies: Array<string> = []) => {
    const path = window.location.pathname
    const isOnExpensePage = path.includes("/expenses") || path.includes("/subscriptions")
    const isOnCurrentDetailPage = isOnExpensePage && path.endsWith(documentId)

    const { expenseId, folderId: currentFolderId } = extractExpenseDetailPagePathname(path)

    // Need to handle case where this method is invoked when window.location is not in the expense detail page, hence the fallback.
    const currentDocId = expenseId ?? path.split("/").pop()

    const folderIsAffected = currentFolderId && dependencies.includes(currentFolderId)

    const isAffectedByBulkOperation = isOnExpensePage && currentDocId && (dependencies.includes(currentDocId) || folderIsAffected)

    return {
        currentDocId,
        currentFolderId,
        isOnExpensePage,
        isOnCurrentDetailPage,
        isAffectedByBulkOperation,
        folderIsAffected,
    }
}

export const getCurrentPagePathParams = () => {
    const path = window.location.pathname
    const pathParts = path.split("/")

    return {
        domain: pathParts.shift(),
        documentId: pathParts.shift(),
        subDocumentId: pathParts.shift(),
    }
}

export const getDashboardTablesForStatus = (status: ExpenseStatusEnum) => {
    switch (status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
            return [TablesEnum.TODO_PURCHASE_APPROVAL, TablesEnum.TODO_APPROVAL_PENDING]
        case ExpenseStatusEnum.DOCS_NEEDED:
            return [TablesEnum.TODO_DOCS_NEEDED, TablesEnum.TODO_APPROVAL_PENDING]
        case ExpenseStatusEnum.INVOICE_PENDING:
            return [TablesEnum.TODO_INVOICE_APPROVAL, TablesEnum.TODO_APPROVAL_PENDING, TablesEnum.TRIP_FOLDER_DRAFT]
        case ExpenseStatusEnum.APPROVED:
            return [TablesEnum.REVIEW_REQUESTS, TablesEnum.IN_PROGRESS]
        case ExpenseStatusEnum.REVIEWED:
            return [TablesEnum.PAY_AND_EXPORT_TO_BE_PAID, TablesEnum.IN_PROGRESS]
        case ExpenseStatusEnum.PAID:
            return [TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED, TablesEnum.IN_PROGRESS]
        case ExpenseStatusEnum.DECLINED:
        case ExpenseStatusEnum.DONE:
            return [TablesEnum.DONE_REQUESTS]
        default:
            return []
    }
}

const extractExpenseDetailPagePathname = (pathname: string) => {
    const folderPathInfo = matchPath<{ id: string; childId: string }>(pathname, { path: CompanyScopedRoutePathEnum.FOLDER_EXPENSE_DETAIL })
    if (folderPathInfo) {
        return {
            folderId: folderPathInfo.params.id,
            expenseId: folderPathInfo.params.childId,
        }
    }
    const expensePathInfo = matchPath<{ id: string }>(pathname, { path: CompanyScopedRoutePathEnum.EXPENSE_DETAIL })
    if (expensePathInfo) {
        return {
            expenseId: expensePathInfo?.params.id,
        }
    }
    const subscriptionPathInfo = matchPath<{ id: string }>(pathname, { path: CompanyScopedRoutePathEnum.SUBSCRIPTION_DETAIL })
    return {
        expenseId: subscriptionPathInfo?.params.id,
    }
}

export const hasChildren = (expenses: Array<Expense>) => {
    return expenses.some((expense) => isFolderExpense(expense) && (expense as TripFolder)?.children.length)
}

export const hasReminders = (expenses: Array<Expense>) => {
    return expenses.some((expense) => expense?.reminders?.length)
}
